import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const CallCenterB2B = () => {
  const jsonLdBreadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Accueil", "item": "https://ziexter.com/" },
      { "@type": "ListItem", "position": 2, "name": "Services", "item": "https://ziexter.com/services" },
      { "@type": "ListItem", "position": 3, "name": "Call Center B2B", "item": "https://ziexter.com/services/call-center-b2b" }
    ]
  };

  return (
    <>
      <Helmet>
        <title>Call Center B2B - ZiExter | Prospection et prise de RDV qualifiés</title>
        <meta name="description" content="Externalisez votre prospection B2B avec ZiExter : prise de rendez-vous qualifiés, qualification de leads, télémarketing. Rationalisez votre prospection, augmentez vos ventes." />
        <link rel="canonical" href="https://ziexter.com/services/call-center-b2b" />
        <script type="application/ld+json">{JSON.stringify(jsonLdBreadcrumb)}</script>
      </Helmet>

      {/* Hero */}
      <section className="page-hero bg-gradient-to-br from-secondary to-primary text-white">
        <div className="container-custom">
          <div className="max-w-3xl animate-fade-in-up">
            <nav className="flex items-center space-x-2 text-sm mb-4 text-gray-200">
              <Link to="/" className="hover:text-white">Accueil</Link>
              <span>/</span>
              <Link to="/services" className="hover:text-white">Services</Link>
              <span>/</span>
              <span className="text-white">Call Center B2B</span>
            </nav>
            <h1 className="font-heading text-4xl md:text-5xl font-bold mb-6">
              ZiExter – Call Center B2B Outbound
            </h1>
            <p className="text-xl text-gray-100 mb-4">
              Rationalisez votre prospection, augmentez vos ventes
            </p>
            <p className="text-lg text-gray-200">
              Vous êtes submergé par la prospection commerciale ? Embaucher en interne ou déléguer à des freelances est souvent coûteux et risqué. Les formations prennent du temps, et la sécurité des données reste une préoccupation majeure.
            </p>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center mb-12 animate-fade-in-up">
            <p className="text-xl text-gray-700 mb-6">
              Avec ZiExter, gagnez du temps et boostez vos ventes grâce à notre call center B2B spécialisé en prospection outbound. Nos équipes appellent non-stop vos prospects sur vos bases de données ou sur les nôtres, pour générer des leads qualifiés et des rendez-vous commerciaux.
            </p>
            <Link to="/devis" className="btn btn-primary btn-lg">
              DEMANDER UN DEVIS
            </Link>
          </div>
        </div>
      </section>

      {/* Autonomisez votre développement commercial */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-12 animate-fade-in-up">
            <h2 className="section-title">Autonomisez votre développement commercial</h2>
            <p className="section-subtitle">
              Chez ZiExter, nous fournissons des équipes qualifiées pour chaque secteur d'activité afin de maximiser vos résultats. Nos agents spécialisés en B2B sont formés à la prospection téléphonique, à la qualification des prospects et à la prise de rendez-vous. Découvrez nos services adaptés à vos besoins :
            </p>
          </div>

          <div className="mb-12">
            <h3 className="text-2xl font-bold text-center mb-6">Services de prospection B2B</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                'Prospection téléphonique outbound',
                'Qualification de prospects et leads',
                'Prise de rendez-vous qualifiés pour vos commerciaux',
                'Enrichissement et mise à jour de bases de données',
                'Relances clients et suivi post-campagne',
                'Campagnes multi-secteurs personnalisées'
              ].map((item, i) => (
                <div key={i} className="card hover-lift animate-fade-in-up" style={{ animationDelay: `${i * 50}ms` }}>
                  <div className="flex items-start">
                    <svg className="w-6 h-6 text-primary mr-3 flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <p className="font-medium">{item}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Domaines d'expertise */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-12 animate-fade-in-up">
            <h2 className="section-title">Domaines d'expertise</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="card hover-lift animate-fade-in-up">
              <h3 className="text-xl font-bold mb-4">Soutien administratif commercial</h3>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-start">
                  <span className="text-primary mr-2">•</span>
                  Gestion des appels entrants et sortants
                </li>
                <li className="flex items-start">
                  <span className="text-primary mr-2">•</span>
                  Suivi des leads dans votre CRM
                </li>
                <li className="flex items-start">
                  <span className="text-primary mr-2">•</span>
                  Gestion des agendas et prise de rendez-vous
                </li>
                <li className="flex items-start">
                  <span className="text-primary mr-2">•</span>
                  Reporting et suivi de performance
                </li>
              </ul>
            </div>

            <div className="card hover-lift animate-fade-in-up animation-delay-100">
              <h3 className="text-xl font-bold mb-4">Support commercial spécialisé</h3>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-start">
                  <span className="text-primary mr-2">•</span>
                  Prospection pour IT, finance, immobilier, assurance, industrie, services B2B
                </li>
                <li className="flex items-start">
                  <span className="text-primary mr-2">•</span>
                  Campagnes personnalisées pour atteindre vos cibles stratégiques
                </li>
                <li className="flex items-start">
                  <span className="text-primary mr-2">•</span>
                  Gestion multi-canaux : téléphone, e-mail, LinkedIn
                </li>
              </ul>
            </div>

            <div className="card hover-lift animate-fade-in-up animation-delay-200">
              <h3 className="text-xl font-bold mb-4">Génération de leads et ventes</h3>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-start">
                  <span className="text-primary mr-2">•</span>
                  Télémarketing B2B efficace
                </li>
                <li className="flex items-start">
                  <span className="text-primary mr-2">•</span>
                  Qualification des leads et opportunités commerciales
                </li>
                <li className="flex items-start">
                  <span className="text-primary mr-2">•</span>
                  Relance clients, upsell et fidélisation
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Pourquoi choisir ZiExter */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-12 animate-fade-in-up">
            <h2 className="section-title">Pourquoi choisir ZiExter ?</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { title: 'Productivité maximale', desc: 'Prospection continue pour remplir vos agendas' },
              { title: 'Flexibilité', desc: 'Campagnes ponctuelles ou permanentes' },
              { title: 'Bases de données fiables', desc: 'Vos fichiers ou les nôtres, mis à jour et sécurisés' },
              { title: 'Sécurité et conformité', desc: 'Respect strict des normes RGPD' },
              { title: 'Équipe spécialisée', desc: 'Agents formés selon vos besoins sectoriels' }
            ].map((item, i) => (
              <div key={i} className="card hover-lift animate-fade-in-up" style={{ animationDelay: `${i * 50}ms` }}>
                <h3 className="font-semibold text-lg mb-2 text-primary">{item.title}</h3>
                <p className="text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Méthodologie */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-12 animate-fade-in-up">
            <h2 className="section-title">Notre méthodologie</h2>
          </div>

          <div className="max-w-4xl mx-auto space-y-6">
            {[
              { num: '1', title: 'Audit & conseil', desc: 'nous analysons vos besoins et votre marché' },
              { num: '2', title: 'Stratégie de prospection', desc: 'scripts personnalisés, ciblage précis' },
              { num: '3', title: 'Exécution', desc: 'appels, qualification, prise de rendez-vous' },
              { num: '4', title: 'Reporting en temps réel', desc: 'suivi détaillé et optimisation continue' },
              { num: '5', title: 'Suivi post-campagne', desc: 'retour d\'expérience et recommandations' }
            ].map((item, i) => (
              <div key={i} className="card flex items-start hover-lift animate-fade-in-up" style={{ animationDelay: `${i * 50}ms` }}>
                <div className="w-12 h-12 bg-primary text-white rounded-full flex items-center justify-center font-bold text-xl mr-4 flex-shrink-0">
                  {item.num}
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-1">{item.title}</h3>
                  <p className="text-gray-600">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Témoignages */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-12 animate-fade-in-up">
            <h2 className="section-title">Témoignages clients</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="card hover-lift animate-fade-in-up">
              <p className="text-gray-700 mb-4 italic">
                « ZiExter a transformé notre prospection : nos commerciaux ne contactent plus que des leads qualifiés. Résultats immédiats ! »
              </p>
              <div className="font-semibold">Philippe Chauvin</div>
            </div>

            <div className="card hover-lift animate-fade-in-up animation-delay-100">
              <p className="text-gray-700 mb-4 italic">
                « En quelques semaines, notre pipeline B2B a explosé grâce à leurs campagnes outbound. »
              </p>
              <div className="font-semibold">Mariane Vallee</div>
            </div>

            <div className="card hover-lift animate-fade-in-up animation-delay-200">
              <p className="text-gray-700 mb-4 italic">
                « Les équipes ZiExter sont efficaces, réactives et très professionnelles. Je recommande ! »
              </p>
              <div className="font-semibold">Enoch Lefevre</div>
            </div>
          </div>
        </div>
      </section>

      {/* Comment ça marche */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-12 animate-fade-in-up">
            <h2 className="section-title">Comment ça marche ?</h2>
          </div>

          <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-6">
            {[
              'Vous définissez vos objectifs et votre cible',
              'Nous configurons la campagne sur vos bases ou les nôtres',
              'Nos agents contactent vos prospects en continu',
              'Vous recevez un reporting détaillé avec leads qualifiés et rendez-vous'
            ].map((item, i) => (
              <div key={i} className="card flex items-start hover-lift animate-fade-in-up" style={{ animationDelay: `${i * 50}ms` }}>
                <div className="w-8 h-8 bg-accent text-white rounded-full flex items-center justify-center font-bold mr-3 flex-shrink-0">
                  {i + 1}
                </div>
                <p className="text-gray-700">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-12 animate-fade-in-up">
            <h2 className="section-title">FAQ – Call Center B2B ZiExter</h2>
          </div>

          <div className="max-w-4xl mx-auto space-y-4">
            <details className="card cursor-pointer">
              <summary className="font-semibold p-4 flex justify-between items-center">
                <span>Quels types de prospects pouvez-vous contacter ?</span>
                <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </summary>
              <div className="p-4 pt-0 text-gray-600">
                Toutes entreprises B2B selon vos critères sectoriels et géographiques.
              </div>
            </details>

            <details className="card cursor-pointer">
              <summary className="font-semibold p-4 flex justify-between items-center">
                <span>Peut-on utiliser nos propres bases de données ?</span>
                <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </summary>
              <div className="p-4 pt-0 text-gray-600">
                Oui, ou nous pouvons fournir nos fichiers qualifiés et à jour.
              </div>
            </details>

            <details className="card cursor-pointer">
              <summary className="font-semibold p-4 flex justify-between items-center">
                <span>Les rendez-vous sont-ils garantis ?</span>
                <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </summary>
              <div className="p-4 pt-0 text-gray-600">
                Nous qualifions les leads pour maximiser vos chances de conversion, mais le succès final dépend de votre suivi commercial.
              </div>
            </details>

            <details className="card cursor-pointer">
              <summary className="font-semibold p-4 flex justify-between items-center">
                <span>Comment garantissez-vous la confidentialité des données ?</span>
                <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </summary>
              <div className="p-4 pt-0 text-gray-600">
                Tous nos agents respectent le RGPD et les protocoles de sécurité stricts.
              </div>
            </details>

            <details className="card cursor-pointer">
              <summary className="font-semibold p-4 flex justify-between items-center">
                <span>Peut-on tester une campagne avant engagement ?</span>
                <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </summary>
              <div className="p-4 pt-0 text-gray-600">
                Oui, nous proposons une phase pilote pour évaluer nos performances.
              </div>
            </details>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-gradient-to-r from-primary to-secondary text-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center animate-fade-in-up">
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
              Engagez ZiExter dès aujourd'hui
            </h2>
            <p className="text-xl text-gray-100 mb-8">
              Ne perdez plus de temps sur des tâches chronophages. Confiez votre prospection à une équipe B2B expérimentée et concentrez-vous sur la conversion et la croissance de votre entreprise.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="btn btn-accent btn-lg">
                CONTACTEZ-NOUS
              </Link>
              <Link to="/devis" className="btn btn-outline-white btn-lg">
                Demander un devis
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default CallCenterB2B;
